export const FILTERS_STORAGE_KEY = 'distributionFilters'
export const FILTERS_VERSION = 3
export const TABLE_STORAGE_KEY = 'distributionTableState'
export const TABLE_STATE_VERSION = 2
export const PAGE_SIZE = 100

export const CLIENT_CATEGORIES = ['top', 'key', 'regular']

export const COLUMN_IDS = [
  'critical',
  'status',
  'distr_id',
  'departure.planed_date',
  'closing_urgency',
  'departure.city_name',
  'destination.city_name',
  'cargo',
  'order.veh.type',
  'tariff',
  'payment',
  'display_name',
  'client_category',
  'order.meta.client_name',
  'worker',
  'actions',
]

export const SORTABLE_COLUMN_IDS = [
  'departure.planed_date',
  'departure.city_name',
  'destination.city_name',
  'order.veh.type',
  'display_name',
  'worker',
  'closing_urgency',
]

export function localToday(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function defaultFilters(today = localToday()) {
  return {
    trip_id: '',
    source: [],
    fromPoints: [],
    toPoints: [],
    clientCategories: [],
    clientNames: [],
    vehicleType: '',
    worker: '',
    loadingDateFrom: today,
    loadingDateTo: today,
    status: [1],
    page: 1,
    highlightMode: 'none',
    highlightPalette: 'colorful',
  }
}

const HIGHLIGHT_MODES = ['none', 'platform', 'tariff', 'clientCategory', 'closingStatus', 'departureCity', 'departureDay', 'urgency', 'status', 'simple']
const HIGHLIGHT_PALETTES = ['colorful', 'zebra', 'bee']

function stringArray(value) {
  return Array.isArray(value) ? [...new Set(value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim()))] : []
}

function categoryArray(value) {
  return Array.isArray(value) ? [...new Set(value.filter((item) => CLIENT_CATEGORIES.includes(item)))] : []
}

export function normalizeFilters(value, today = localToday()) {
  const fallback = defaultFilters(today)
  const source = value && typeof value === 'object' ? value : {}
  return {
    trip_id: typeof source.trip_id === 'string' ? source.trip_id : fallback.trip_id,
    source: stringArray(source.source),
    fromPoints: stringArray(source.fromPoints),
    toPoints: stringArray(source.toPoints),
    clientCategories: categoryArray(source.clientCategories),
    clientNames: stringArray(source.clientNames),
    vehicleType: typeof source.vehicleType === 'string' ? source.vehicleType : fallback.vehicleType,
    worker: typeof source.worker === 'string' ? source.worker : fallback.worker,
    loadingDateFrom: typeof source.loadingDateFrom === 'string' ? source.loadingDateFrom : fallback.loadingDateFrom,
    loadingDateTo: typeof source.loadingDateTo === 'string' ? source.loadingDateTo : fallback.loadingDateTo,
    status: Array.isArray(source.status) ? [...new Set(source.status.map(Number).filter(Number.isFinite))] : fallback.status,
    page: Number.isInteger(source.page) && source.page > 0 ? source.page : fallback.page,
    highlightMode: HIGHLIGHT_MODES.includes(source.highlightMode) ? source.highlightMode : fallback.highlightMode,
    highlightPalette: HIGHLIGHT_PALETTES.includes(source.highlightPalette) ? source.highlightPalette : fallback.highlightPalette,
  }
}

export function loadFilters(storage, today = localToday()) {
  try {
    const parsed = JSON.parse(storage?.getItem(FILTERS_STORAGE_KEY) || 'null')
    if (parsed?.filtersVersion === 1) return normalizeFilters(parsed, today)
    if (parsed?.version !== FILTERS_VERSION) return defaultFilters(today)
    return normalizeFilters(parsed.value, today)
  } catch {
    return defaultFilters(today)
  }
}

export function saveFilters(storage, filters) {
  storage?.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ version: FILTERS_VERSION, value: normalizeFilters(filters) }))
}

export function apiSortsToTanstack(sorts) {
  return (Array.isArray(sorts) ? sorts : []).map((sort) => ({ id: sort.field, desc: Number(sort.dir) === -1 }))
}

export function tanstackSortsToApi(sorting) {
  return (Array.isArray(sorting) ? sorting : []).map((sort) => ({ field: sort.id, dir: sort.desc ? -1 : 1 }))
}

export function buildDistributionParams(filters, sorting, { includePaging = true } = {}) {
  const normalized = normalizeFilters(filters)
  const sorts = tanstackSortsToApi(sorting)
  const firstSort = sorts[0]
  const workerSort = firstSort?.field === 'worker'
  const params = {
    trip_id: normalized.trip_id || undefined,
    display_name: normalized.source.length ? normalized.source : undefined,
    from_points: normalized.fromPoints.length ? normalized.fromPoints : undefined,
    to_points: normalized.toPoints.length ? normalized.toPoints : undefined,
    client_categories: normalized.clientCategories.length ? normalized.clientCategories : undefined,
    client_names: normalized.clientNames.length ? normalized.clientNames : undefined,
    vehicle_type: normalized.vehicleType || undefined,
    worker: normalized.worker || undefined,
    loading_date_from: normalized.loadingDateFrom || undefined,
    loading_date_to: normalized.loadingDateTo || undefined,
    status: normalized.status.length ? normalized.status : undefined,
    sort: !workerSort ? sorts.map((sort) => `${sort.field}:${sort.dir}`).join(',') : undefined,
    sort_by: workerSort ? 'worker' : undefined,
    sort_dir: firstSort?.dir,
  }
  params.limit = includePaging ? PAGE_SIZE : 10000
  params.skip = includePaging ? (normalized.page - 1) * PAGE_SIZE : 0
  return params
}

export function normalizeTableState(value, defaultSorting) {
  const source = value && typeof value === 'object' ? value : {}
  const known = new Set(COLUMN_IDS)
  const sortable = new Set([...SORTABLE_COLUMN_IDS, 'departure.planed_date_day'])
  const order = Array.isArray(source.columnOrder)
    ? [...new Set(source.columnOrder.filter((id) => known.has(id)))]
    : []
  const missing = COLUMN_IDS.filter((id) => !order.includes(id))
  const visibility = {}
  if (source.columnVisibility && typeof source.columnVisibility === 'object') {
    COLUMN_IDS.forEach((id) => {
      if (typeof source.columnVisibility[id] === 'boolean') visibility[id] = source.columnVisibility[id]
    })
  }
  if (COLUMN_IDS.every((id) => visibility[id] === false)) visibility[COLUMN_IDS[0]] = true
  const sizing = {}
  if (source.columnSizing && typeof source.columnSizing === 'object') {
    COLUMN_IDS.forEach((id) => {
      const size = Number(source.columnSizing[id])
      if (Number.isFinite(size) && size >= 18 && size <= 600) sizing[id] = size
    })
  }
  const sorting = Array.isArray(source.sorting)
    ? source.sorting.filter((item) => sortable.has(item?.id) && typeof item.desc === 'boolean').map((item) => ({ id: item.id, desc: item.desc }))
    : []
  return {
    sorting: sorting.length ? sorting : defaultSorting.map((item) => ({ ...item })),
    columnOrder: [...order, ...missing],
    columnVisibility: visibility,
    columnSizing: sizing,
  }
}

export function loadTableState(storage, defaultSorting) {
  try {
    const parsed = JSON.parse(storage?.getItem(TABLE_STORAGE_KEY) || 'null')
    if (parsed?.version !== TABLE_STATE_VERSION) return normalizeTableState({}, defaultSorting)
    return normalizeTableState(parsed.value, defaultSorting)
  } catch {
    return normalizeTableState({}, defaultSorting)
  }
}

export function saveTableState(storage, state, defaultSorting) {
  storage?.setItem(TABLE_STORAGE_KEY, JSON.stringify({
    version: TABLE_STATE_VERSION,
    value: normalizeTableState(state, defaultSorting),
  }))
}

export function reorderColumns(order, movedId, targetId) {
  const result = [...order]
  const from = result.indexOf(movedId)
  const to = result.indexOf(targetId)
  if (from < 0 || to < 0 || from === to) return result
  result.splice(from, 1)
  result.splice(to, 0, movedId)
  return result
}
